import { useEffect, useState } from 'react';
import AuthService from '../../app/auth/Authorization.service';

const BASE_URL = process.env.REACT_APP_AUTH_SERVER_BASE_URL ?? '';

/**
 * Resolves a stored file key to a displayable URL.
 *
 * `GET /files/:file_key` is inside the auth group, so an <img src> pointed at it
 * gets a 401 — the tag cannot carry a Bearer header. Fetch it as a blob instead
 * and hand back an object URL, revoked when the key changes or the caller unmounts.
 *
 * Pass `undefined` to skip the fetch entirely (no key yet, or the caller supplies
 * its own URL).
 */
export default function useAuthedImage(fileKey?: string) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!fileKey) {
      setUrl(null);
      setLoading(false);
      setError(false);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    setLoading(true);
    setError(false);
    setUrl(null);

    fetch(`${BASE_URL}/files/${fileKey}`, {
      headers: { Authorization: `Bearer ${AuthService.getAccessToken()}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileKey]);

  return { url, loading, error };
}
